import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { ADMIN_NAV, findNavContext } from '../config/navigation';
import { adminEnvironment } from '../config/environment';
import CommandPalette from '../components/shell/CommandPalette';
import DesktopAgentIndicator from '../components/shell/DesktopAgentIndicator';
import { DesktopAgentProvider } from '../contexts/DesktopAgentContext';
import logoFull from '../../../tools/src/assets/Logo_Horizontal_Full.png';
import logoIcon from '../../../tools/src/assets/Logo_Only_Icon.png';
import '../styles/shell.scss';

/**
 * Cornice dell'app admin, allineata al portale Semplora: sidebar chiara con
 * le stesse proporzioni e lo stesso logo, header compatto con breadcrumb,
 * ambiente (Locale/UAT/Produzione) sempre visibile e palette comandi.
 *
 * La sidebar si comprime a sole icone (stato ricordato nel browser) e su
 * schermi stretti diventa un pannello a scomparsa.
 */
const COLLAPSE_KEY = 'admin.sidebarCollapsed';

const readCollapsed = (): boolean => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};

const initialsOf = (email?: string | null, name?: string | null): string => {
    const source = (name || email || '?').trim();
    const parts = source.split(/[\s.@_-]+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
};

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

const AdminShell: React.FC = () => {
    const { user, signOutAdmin } = useAuth();
    const { pathname } = useLocation();
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const { section, item } = findNavContext(pathname);

    const toggleCollapsed = () => {
        setCollapsed(c => {
            const next = !c;
            try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* storage non disponibile */ }
            return next;
        });
    };

    useEffect(() => { setMobileOpen(false); }, [pathname]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setPaletteOpen(o => !o);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const closePalette = useCallback(() => setPaletteOpen(false), []);
    const handleSignOut = useCallback(() => { void signOutAdmin(); }, [signOutAdmin]);

    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Admin';

    return (
        <DesktopAgentProvider>
        <div className={`admin-shell ${collapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'is-mobile-open' : ''}`}>
            <aside className="admin-sidebar" aria-label="Navigazione admin">
                <Link to="/dashboard" className="admin-sidebar__brand" title="Torna alla dashboard">
                    <img src={logoFull} alt="Semplora" className="admin-sidebar__logo is-full" />
                    <img src={logoIcon} alt="Semplora" className="admin-sidebar__logo is-icon" />
                    <span className="admin-sidebar__badge">Admin</span>
                </Link>

                <nav className="admin-sidebar__nav">
                    {ADMIN_NAV.map(group => (
                        <div key={group.label} className="admin-sidebar__group">
                            <div className="admin-sidebar__label">{group.label}</div>
                            {group.items.map(navItem => (
                                <NavLink
                                    key={navItem.to}
                                    to={navItem.to}
                                    title={collapsed ? navItem.label : undefined}
                                    className={({ isActive }) => `admin-sidebar__item ${isActive ? 'is-active' : ''}`}>
                                    <span className="admin-sidebar__icon">
                                        {navItem.productIcon
                                            ? <img className="nav-product-icon" src={navItem.productIcon} alt="" aria-hidden="true" />
                                            : <i className={`bi ${navItem.icon}`} aria-hidden="true" />}
                                    </span>
                                    <span className="admin-sidebar__text">{navItem.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="admin-sidebar__footer">
                    <button type="button" className="admin-sidebar__item" onClick={toggleCollapsed}
                        title={collapsed ? 'Espandi menu' : undefined}
                        aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}>
                        <span className="admin-sidebar__icon">
                            <i className={`bi ${collapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`} aria-hidden="true" />
                        </span>
                        <span className="admin-sidebar__text">Comprimi menu</span>
                    </button>
                </div>
            </aside>

            {mobileOpen && <div className="admin-shell__overlay" onClick={() => setMobileOpen(false)} />}

            <div className="admin-main">
                <header className="admin-topbar">
                    <button type="button" className="admin-topbar__icon-btn d-lg-none" onClick={() => setMobileOpen(true)} aria-label="Apri menu">
                        <i className="bi bi-list" />
                    </button>

                    <nav className="admin-topbar__crumbs" aria-label="Percorso">
                        <Link to="/dashboard">Admin</Link>
                        {section && <><i className="bi bi-chevron-right" aria-hidden="true" /><span>{section.label}</span></>}
                        {item && <><i className="bi bi-chevron-right" aria-hidden="true" /><strong>{item.label}</strong></>}
                    </nav>

                    <button type="button" className="admin-topbar__search" onClick={() => setPaletteOpen(true)}>
                        <i className="bi bi-search" aria-hidden="true" />
                        <span>Cerca pagine e tool</span>
                        <kbd>{isMac ? '⌘' : 'Ctrl'} K</kbd>
                    </button>

                    <span className={`env-pill is-${adminEnvironment.kind}`} title="Ambiente su cui stai operando">
                        <i aria-hidden="true" />
                        {adminEnvironment.label}
                    </span>

                    <DesktopAgentIndicator />

                    <Dropdown align="end">
                        <Dropdown.Toggle as="button" className="admin-topbar__user" aria-label="Menu utente">
                            <span className="admin-avatar">{initialsOf(user?.email, user?.displayName)}</span>
                            <span className="admin-topbar__user-text d-none d-md-flex">
                                <strong>{displayName}</strong>
                                <small>Amministratore</small>
                            </span>
                            <i className="bi bi-chevron-down small d-none d-md-inline" aria-hidden="true" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Header className="small">
                                Accesso come<br /><span className="text-body fw-semibold">{user?.email ?? '—'}</span>
                            </Dropdown.Header>
                            <Dropdown.Divider />
                            <Dropdown.Item as={Link} to="/audit">
                                <i className="bi bi-shield-check me-2" />Audit log
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleSignOut} className="text-danger">
                                <i className="bi bi-box-arrow-right me-2" />Esci
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </header>

                <main className="admin-content" id="admin-content">
                    <Outlet />
                </main>
            </div>

            <CommandPalette open={paletteOpen} onClose={closePalette} onSignOut={handleSignOut} />
        </div>
        </DesktopAgentProvider>
    );
};

export default AdminShell;
